
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from 'react';

/**
 * Custom hook to update document head for SEO.
 * @param title - The page title (will be suffixed with " | Constantina").
 * @param description - The meta description content.
 * @param keywords - Array of keywords to set in meta tags.
 * @param canonicalUrl - Optional canonical URL for the page.
 * @param imageUrl - Optional image URL for Open Graph and Twitter Card.
 */
export const useSEO = (
  title: string,
  description: string,
  keywords: string[] = [],
  canonicalUrl?: string,
  imageUrl?: string
) => {
  useEffect(() => {
    const fullTitle = `${title} | Constantina Style Advisor`;
    const currentUrl = canonicalUrl || window.location.href.split('?')[0];
    const defaultImage = imageUrl || 'https://www.constantinalifestyle.one/favicon.png';

    // Update Title
    document.title = fullTitle;

    // Helper function to set/update meta tag
    const setMetaTag = (selector: string, attribute: string, name: string, content: string) => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard Meta Tags
    setMetaTag("meta[name='description']", 'name', 'description', description);

    if (keywords.length > 0) {
      setMetaTag("meta[name='keywords']", 'name', 'keywords', keywords.join(', '));
    }

    // Open Graph Meta Tags
    setMetaTag("meta[property='og:title']", 'property', 'og:title', fullTitle);
    setMetaTag("meta[property='og:description']", 'property', 'og:description', description);
    setMetaTag("meta[property='og:image']", 'property', 'og:image', defaultImage);
    setMetaTag("meta[property='og:url']", 'property', 'og:url', currentUrl);
    setMetaTag("meta[property='og:type']", 'property', 'og:type', 'website');
    setMetaTag("meta[property='og:site_name']", 'property', 'og:site_name', 'Constantina Lifestyle');

    // Twitter Card Meta Tags
    setMetaTag("meta[name='twitter:card']", 'name', 'twitter:card', 'summary_large_image');
    setMetaTag("meta[name='twitter:title']", 'name', 'twitter:title', fullTitle);
    setMetaTag("meta[name='twitter:description']", 'name', 'twitter:description', description);
    setMetaTag("meta[name='twitter:image']", 'name', 'twitter:image', defaultImage);
    setMetaTag("meta[name='twitter:site']", 'name', 'twitter:site', '@constantinalifestyle');

    // Update Canonical URL
    let linkCanonical = document.querySelector("link[rel='canonical']");
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', currentUrl);

  }, [title, description, keywords, canonicalUrl, imageUrl]);
};
